import React from 'react'
import { CompanyRegistryEvent, CVROwnershipStructure, CVRParticipant } from '../../types'
import { participantCountryFlagCode } from '../../utils/format'
import { collectRecentCompanyActivity } from '../../utils/companyActivity'

/**
 * High-signal one-liner that sits directly under the IdentityBar. It
 * compresses the journalistic essence of a company into a single row
 * of clickable counts:
 *
 *   2 reelle ejere · 1 udenlandsk · 3 ændringer (12 mdr.)
 *
 * Each count is a button that selects the relevant tab — so it doubles
 * as a navigation shortcut.
 *
 * Counts are derived in this file (rather than at the page level) so
 * the rules stay close to the rendering: no surprise side-effects, no
 * cross-component coupling.
 */
const RECENT_WINDOW_DAYS = 366

interface SummaryStripProps {
  ownership: CVROwnershipStructure
  registryEvents?: CompanyRegistryEvent[]
  onJump: (tab: 'ejerskab' | 'aktivitet' | 'ledelse') => void
  /** Override "now" for deterministic test output. */
  now?: Date
}

interface SummaryCounts {
  activeBeneficial: number
  foreignBeneficial: number
  recentChanges: number
  management: number
}

export function deriveSummaryCounts(
  ownership: CVROwnershipStructure,
  reference: Date,
  founders: CVRParticipant[] = [],
  registryEvents: CompanyRegistryEvent[] = []
): SummaryCounts {
  const activeBO = ownership.beneficialOwners.filter((p) => p.role.isActive)
  const foreignBO = activeBO.filter((p) => participantCountryFlagCode(p) != null)

  const recent = collectRecentCompanyActivity(
    ownership,
    registryEvents,
    reference,
    RECENT_WINDOW_DAYS,
    founders
  ).length

  const management = [
    ...ownership.management,
    ...ownership.boardMembers,
    ...ownership.auditors,
  ].filter((p) => p.role.isActive).length

  return {
    activeBeneficial: activeBO.length,
    foreignBeneficial: foreignBO.length,
    recentChanges: recent,
    management,
  }
}

const SummaryStrip: React.FC<SummaryStripProps> = ({
  ownership,
  registryEvents = [],
  onJump,
  now,
}) => {
  const counts = deriveSummaryCounts(ownership, now ?? new Date(), [], registryEvents)

  const items: Array<{
    key: string
    label: string
    tone: 'neutral' | 'signal'
    tab: 'ejerskab' | 'aktivitet' | 'ledelse'
  }> = []

  if (counts.activeBeneficial === 0) {
    items.push({
      key: 'no-bo',
      label: 'Ingen reelle ejere registreret',
      tone: 'signal',
      tab: 'ejerskab',
    })
  } else {
    items.push({
      key: 'bo',
      label: `${counts.activeBeneficial} reel${counts.activeBeneficial === 1 ? '' : 'le'} ejer${
        counts.activeBeneficial === 1 ? '' : 'e'
      }`,
      tone: 'neutral',
      tab: 'ejerskab',
    })
  }

  if (counts.foreignBeneficial > 0) {
    items.push({
      key: 'foreign',
      label: `${counts.foreignBeneficial} udenlandsk${counts.foreignBeneficial === 1 ? '' : 'e'}`,
      tone: 'signal',
      tab: 'ejerskab',
    })
  }

  items.push({
    key: 'changes',
    label:
      counts.recentChanges === 0
        ? 'Ingen aktivitet (12 mdr.)'
        : `${counts.recentChanges} aktivitet${counts.recentChanges === 1 ? '' : 'er'} (12 mdr.)`,
    tone: counts.recentChanges > 0 ? 'neutral' : 'neutral',
    tab: 'aktivitet',
  })

  if (counts.management > 0) {
    items.push({
      key: 'mgmt',
      label: `${counts.management} ledelsesrolle${counts.management === 1 ? '' : 'r'}`,
      tone: 'neutral',
      tab: 'ledelse',
    })
  }

  return (
    <div
      data-testid="summary-strip"
      className="-mt-2 pb-1 flex flex-wrap items-center gap-x-1 gap-y-2 text-sm"
    >
      {items.map((item, idx) => (
        <React.Fragment key={item.key}>
          {idx > 0 && (
            <span aria-hidden className="text-ink-400 select-none">
              ·
            </span>
          )}
          <button
            type="button"
            onClick={() => onJump(item.tab)}
            className={`px-1 -mx-1 rounded-sm underline-offset-4 hover:underline decoration-rule-strong hover:decoration-signal focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-400 tabular-nums ${
              item.tone === 'signal' ? 'text-signal-900' : 'text-ink'
            }`}
          >
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  )
}

export default SummaryStrip
